import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

async function resetStudentPassword(classeId, studentId) {
  if (!window.confirm('Réinitialiser le mot de passe de cet étudiant à StageLink2026! ?')) return false;
  await api.post(`/classes/${classeId}/students/${studentId}/reset-password`);
  return true;
}

const fmt = d => new Date(d).toLocaleDateString('fr-FR');

function StudentProfileModal({ student, onClose }) {
  const fields = [
    { label: 'Civilité', value: student.civilite },
    { label: 'Email', value: student.email },
    { label: 'Téléphone', value: student.phone },
    { label: 'Date de naissance', value: student.dateNaissance ? fmt(student.dateNaissance) : null },
    { label: 'INE', value: student.ine },
    { label: 'Adresse', value: student.adresse },
    { label: 'Email parent', value: student.emailParent },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">{student.firstName} {student.lastName}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        <div className="space-y-2">
          {fields.map(({ label, value }) => (
            <div key={label} className="flex justify-between text-sm">
              <span className="text-gray-500 shrink-0 mr-4">{label}</span>
              <span className="text-gray-800 text-right">{value || <span className="text-gray-300 italic">—</span>}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PeriodeManagerTeacher({ classe, onUpdated }) {
  const [periodes, setPeriodes] = useState(classe.periodes || []);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nom: '', startDate: '', endDate: '' });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({ nom: p.nom, startDate: p.startDate.split('T')[0], endDate: p.endDate.split('T')[0] });
    setShowForm(false);
  };

  const cancelEdit = () => { setEditId(null); setForm({ nom: '', startDate: '', endDate: '' }); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (editId) {
        const res = await api.put(`/classes/${classe.id}/periodes/${editId}`, form);
        setPeriodes(prev => prev.map(p => p.id === editId ? res.data : p));
        setEditId(null);
      } else {
        const res = await api.post(`/classes/${classe.id}/periodes`, form);
        setPeriodes(prev => [...prev, res.data]);
        setShowForm(false);
      }
      setForm({ nom: '', startDate: '', endDate: '' });
      if (onUpdated) onUpdated();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette période ?')) return;
    try {
      await api.delete(`/classes/${classe.id}/periodes/${id}`);
      setPeriodes(prev => prev.filter(p => p.id !== id));
      if (onUpdated) onUpdated();
    } catch { alert('Erreur lors de la suppression'); }
  };

  const formFields = (
    <form onSubmit={save} className="flex gap-2 flex-wrap items-end mt-2">
      {error && <p className="w-full text-red-600 text-xs">{error}</p>}
      <div>
        <label className="block text-xs text-gray-500 mb-0.5">Nom</label>
        <input required type="text" value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))}
          placeholder="ex: Stage 1" className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 w-28" />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-0.5">Début</label>
        <input required type="date" value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))}
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-0.5">Fin</label>
        <input required type="date" value={form.endDate} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))}
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs px-3 py-1.5 rounded-lg">
        {saving ? '...' : editId ? 'Enregistrer' : 'Ajouter'}
      </button>
      <button type="button" onClick={editId ? cancelEdit : () => setShowForm(false)} className="text-xs text-gray-500 px-2 py-1.5">Annuler</button>
    </form>
  );

  return (
    <div className="px-5 py-4">
      {periodes.length === 0 && !showForm && (
        <p className="text-sm text-gray-400 mb-2">Aucune période définie pour cette classe.</p>
      )}
      {periodes.length > 0 && (
        <div className="space-y-2 mb-3">
          {periodes.map(p => (
            <div key={p.id}>
              {editId === p.id ? formFields : (
                <div className="flex items-center gap-3 text-sm bg-gray-50 rounded-lg px-3 py-2">
                  <span className="font-medium text-gray-700 w-24">{p.nom}</span>
                  <span className="text-gray-500 flex-1">{fmt(p.startDate)} → {fmt(p.endDate)}</span>
                  <button onClick={() => startEdit(p)} className="text-xs text-blue-500 hover:text-blue-700">Modifier</button>
                  <button onClick={() => remove(p.id)} className="text-xs text-red-400 hover:text-red-600">Suppr.</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {!editId && (showForm ? formFields : (
        <button onClick={() => setShowForm(true)} className="text-xs text-blue-600 hover:text-blue-800 font-medium">+ Ajouter une période</button>
      ))}
    </div>
  );
}

export default function ClasseManager() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [classes, setClasses] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', schoolYearId: '' });
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [students, setStudents] = useState({});
  const [configTab, setConfigTab] = useState({}); // classeId -> 'students' | 'config' | 'periodes'
  const [configForm, setConfigForm] = useState({});
  const [configSaving, setConfigSaving] = useState({});
  const [visiteQuestionsState, setVisiteQuestionsState] = useState({});
  const [visiteQSaving, setVisiteQSaving] = useState({});
  const [visitePdfUploading, setVisitePdfUploading] = useState({});
  const [viewStudent, setViewStudent] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/classes'),
      api.get('/admin/school-years'),
    ]).then(([c, y]) => {
      setClasses(c.data);
      setSchoolYears(y.data);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const loadStudents = async (classeId) => {
    if (students[classeId]) return;
    try {
      const res = await api.get(`/classes/${classeId}/students`);
      setStudents(prev => ({ ...prev, [classeId]: res.data }));
    } catch (e) { console.error(e); }
  };

  const toggle = (id) => {
    if (expanded === id) {
      setExpanded(null);
    } else {
      setExpanded(id);
      loadStudents(id);
      if (!configTab[id]) setConfigTab(prev => ({ ...prev, [id]: 'students' }));
    }
  };

  const openConfig = (c) => {
    setConfigForm(prev => ({
      ...prev,
      [c.id]: {
        missionsDefaut:       c.missionsDefaut       || '',
        competencesDefaut:    c.competencesDefaut    || '',
        modalitesEncadrement: c.modalitesEncadrement || '',
        modalitesEvaluation:  c.modalitesEvaluation  || '',
        travailAFournir:      c.travailAFournir       || '',
      },
    }));
    setVisiteQuestionsState(prev => ({
      ...prev,
      [c.id]: c.visiteQuestions || [],
    }));
  };

  const saveVisiteQuestions = async (classeId) => {
    setVisiteQSaving(prev => ({ ...prev, [classeId]: true }));
    try {
      await api.put(`/classes/${classeId}/visite-questions`, { visiteQuestions: visiteQuestionsState[classeId] || [] });
    } catch (e) { console.error(e); }
    finally { setVisiteQSaving(prev => ({ ...prev, [classeId]: false })); }
  };

  const uploadVisitePdfTemplate = async (classeId, file) => {
    setVisitePdfUploading(prev => ({ ...prev, [classeId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/classes/${classeId}/visite-pdf-template`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } catch (e) {
      console.error(e);
      const msg = e.response?.status === 413
        ? 'Fichier trop volumineux (limite : 20 Mo)'
        : e.response?.data?.error || 'Erreur lors de l\'upload';
      alert(msg);
    } finally {
      setVisitePdfUploading(prev => ({ ...prev, [classeId]: false }));
    }
  };

  const deleteVisitePdfTemplate = async (classeId) => {
    if (!window.confirm('Supprimer le formulaire de visite ?')) return;
    try {
      await api.delete(`/classes/${classeId}/visite-pdf-template`);
      await load();
    } catch (e) {
      console.error(e);
      alert('Erreur lors de la suppression');
    }
  };

  const addQuestion = (classeId) => {
    const newQ = { id: Date.now().toString(), label: '', type: 'text' };
    setVisiteQuestionsState(prev => ({ ...prev, [classeId]: [...(prev[classeId] || []), newQ] }));
  };

  const saveConfig = async (classeId) => {
    setConfigSaving(prev => ({ ...prev, [classeId]: true }));
    try {
      await api.put(`/classes/${classeId}`, configForm[classeId]);
      await load();
    } catch (e) { console.error(e); }
    finally { setConfigSaving(prev => ({ ...prev, [classeId]: false })); }
  };

  const createClasse = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/classes', form);
      setShowForm(false);
      setForm({ name: '', schoolYearId: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    }
  };

  if (loading) return <div className="text-gray-500 text-sm py-8">Chargement...</div>;

  return (
    <div>
      {viewStudent && <StudentProfileModal student={viewStudent} onClose={() => setViewStudent(null)} />}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Mes classes</h2>
        {isAdmin && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            + Nouvelle classe
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={createClasse} className="bg-white rounded-xl border border-gray-200 p-5 mb-6 space-y-4">
          <h3 className="font-medium text-gray-800">Créer une classe</h3>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ex: BTS NDRC 1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Année scolaire</label>
              <select
                value={form.schoolYearId}
                onChange={e => setForm(p => ({ ...p, schoolYearId: e.target.value }))}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choisir</option>
                {schoolYears.map(y => <option key={y.id} value={y.id}>{y.label}{y.isActive ? ' (active)' : ''}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg">Créer</button>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm text-gray-600 px-3 py-2">Annuler</button>
          </div>
        </form>
      )}

      {classes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
          Aucune classe créée
        </div>
      ) : (
        <div className="space-y-3">
          {classes.map(c => (
            <div key={c.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggle(c.id)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="text-left">
                  <p className="font-medium text-gray-800">{c.name}</p>
                  <p className="text-sm text-gray-500">{c.schoolYear?.label}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">{c.students?.length || 0} étudiant(s)</span>
                  <span className="text-gray-400">{expanded === c.id ? '▲' : '▼'}</span>
                </div>
              </button>

              {expanded === c.id && (
                <div className="border-t border-gray-100">
                  {/* Sous-onglets */}
                  <div className="flex gap-4 px-5 pt-3 border-b border-gray-100">
                    {[
                      { id: 'students', label: 'Étudiants' },
                      { id: 'config', label: 'Configuration' },
                      { id: 'periodes', label: 'Périodes de stage' },
                    ].map(tab => (
                      <button key={tab.id} onClick={() => { setConfigTab(prev => ({ ...prev, [c.id]: tab.id })); if (tab.id === 'config') openConfig(c); }}
                        className={`pb-2 text-xs font-medium border-b-2 transition-colors ${(configTab[c.id] || 'students') === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Onglet étudiants */}
                  {(configTab[c.id] || 'students') === 'students' && (
                    <div className="px-5 py-4">
                      {students[c.id] ? (
                        students[c.id].length === 0 ? (
                          <p className="text-sm text-gray-500">Aucun étudiant dans cette classe</p>
                        ) : (
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-gray-500 border-b border-gray-100">
                                <th className="pb-2">Prénom</th>
                                <th className="pb-2">Nom</th>
                                <th className="pb-2">Email</th>
                                <th className="pb-2">INE</th>
                                <th className="pb-2"></th>
                                <th className="pb-2"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {students[c.id].map(s => (
                                <tr key={s.id}>
                                  <td className="py-1.5">{s.firstName}</td>
                                  <td className="py-1.5 font-medium">{s.lastName}</td>
                                  <td className="py-1.5 text-gray-500">{s.email || <span className="italic text-gray-300">sans email</span>}</td>
                                  <td className="py-1.5 text-gray-500">{s.ine}</td>
                                  <td className="py-1.5">
                                    <button onClick={() => setViewStudent(s)}
                                      className="text-xs text-blue-500 hover:text-blue-700">Profil</button>
                                  </td>
                                  <td className="py-1.5 text-right">
                                    {isAdmin && (
                                      <button onClick={() => resetStudentPassword(c.id, s.id).then(ok => ok && alert('Mot de passe réinitialisé : StageLink2026!'))}
                                        className="text-xs text-gray-400 hover:text-orange-600">Réinit. MDP</button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )
                      ) : (
                        <p className="text-sm text-gray-400">Chargement...</p>
                      )}
                    </div>
                  )}

                  {/* Onglet périodes */}
                  {configTab[c.id] === 'periodes' && (
                    <PeriodeManagerTeacher classe={c} onUpdated={load} />
                  )}

                  {/* Onglet configuration */}
                  {configTab[c.id] === 'config' && configForm[c.id] && (
                    <div className="px-5 py-4 space-y-4">
                      {(!c.missionsDefaut || !c.competencesDefaut || !c.modalitesEncadrement || !c.modalitesEvaluation || !c.travailAFournir) && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                          ⚠ Configuration incomplète — les étudiants ne pourront pas soumettre leur convention tant que tous les champs ne sont pas renseignés.
                        </div>
                      )}
                      {[
                        { key: 'missionsDefaut',       label: 'Missions confiées',          rows: 4 },
                        { key: 'competencesDefaut',    label: 'Compétences visées',          rows: 3 },
                        { key: 'modalitesEncadrement', label: 'Modalités d\'encadrement',    rows: 2 },
                        { key: 'modalitesEvaluation',  label: 'Modalités d\'évaluation',     rows: 2 },
                        { key: 'travailAFournir',      label: 'Travail à fournir / livrables', rows: 2 },
                      ].map(({ key, label, rows }) => (
                        <div key={key}>
                          <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                          <textarea rows={rows} value={configForm[c.id][key]}
                            onChange={e => setConfigForm(prev => ({ ...prev, [c.id]: { ...prev[c.id], [key]: e.target.value } }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                        </div>
                      ))}
                      <button onClick={() => saveConfig(c.id)} disabled={configSaving[c.id]}
                        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg">
                        {configSaving[c.id] ? 'Enregistrement...' : 'Enregistrer la configuration'}
                      </button>

                      {/* Formulaire de visite PDF template */}
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">Formulaire de visite (PDF)</p>
                        <p className="text-xs text-gray-500 mb-3">
                          Ce formulaire PDF sera proposé aux enseignants lors des visites de stage. Ils pourront le télécharger, le remplir à la main, puis le déposer scanné.
                        </p>
                        {c.visitePdfTemplate ? (
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-green-600 font-medium">✓ Formulaire déposé</span>
                            <button
                              onClick={() => window.open(`${process.env.REACT_APP_API_URL || ''}/uploads/${c.visitePdfTemplate}`, '_blank')}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              Voir
                            </button>
                            <button
                              onClick={() => deleteVisitePdfTemplate(c.id)}
                              className="text-xs text-red-400 hover:text-red-600"
                            >
                              Supprimer
                            </button>
                          </div>
                        ) : (
                          <div>
                            <input
                              type="file"
                              accept=".pdf"
                              id={`visite-pdf-${c.id}`}
                              className="hidden"
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) uploadVisitePdfTemplate(c.id, file);
                                e.target.value = '';
                              }}
                            />
                            <label
                              htmlFor={`visite-pdf-${c.id}`}
                              className="inline-block px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white text-xs rounded-lg cursor-pointer transition-colors"
                            >
                              {visitePdfUploading[c.id] ? 'Upload...' : 'Déposer un formulaire PDF'}
                            </label>
                          </div>
                        )}
                      </div>

                      {/* Form builder questionnaire de visite */}
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-3">Questionnaire de visite de stage (numérique)</p>
                        <p className="text-xs text-gray-500 mb-3">
                          Ces questions seront posées lors des visites de stage. Les enseignants assignés pourront y répondre depuis le suivi de stage.
                        </p>
                        <div className="space-y-2">
                          {(visiteQuestionsState[c.id] || []).map((q, idx) => (
                            <div key={q.id} className="flex gap-2 items-start">
                              <span className="text-xs text-gray-400 mt-2 w-4">{idx + 1}.</span>
                              <input
                                type="text"
                                value={q.label}
                                onChange={e => setVisiteQuestionsState(prev => ({
                                  ...prev,
                                  [c.id]: prev[c.id].map(item => item.id === q.id ? { ...item, label: e.target.value } : item),
                                }))}
                                placeholder="Libellé de la question"
                                className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <select
                                value={q.type}
                                onChange={e => setVisiteQuestionsState(prev => ({
                                  ...prev,
                                  [c.id]: prev[c.id].map(item => item.id === q.id ? { ...item, type: e.target.value } : item),
                                }))}
                                className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="text">Texte libre</option>
                                <option value="oui_non">Oui / Non</option>
                                <option value="note">Note (1-4)</option>
                              </select>
                              <button
                                type="button"
                                onClick={() => setVisiteQuestionsState(prev => ({
                                  ...prev,
                                  [c.id]: prev[c.id].filter(item => item.id !== q.id),
                                }))}
                                className="text-xs text-red-400 hover:text-red-600 mt-1.5"
                              >
                                Suppr.
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-3 mt-3">
                          <button
                            type="button"
                            onClick={() => addQuestion(c.id)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            + Ajouter une question
                          </button>
                          <button
                            type="button"
                            onClick={() => saveVisiteQuestions(c.id)}
                            disabled={visiteQSaving[c.id]}
                            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-800 disabled:opacity-50 text-white text-xs rounded-lg"
                          >
                            {visiteQSaving[c.id] ? '...' : 'Enregistrer le questionnaire'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
